import { useEffect, useState, useCallback, useRef } from 'react';
import {
  FileText, Upload, Globe, Building2, FolderOpen, Trash2, RefreshCw,
  CheckCircle, XCircle, Loader2, X, History, MapPin, ChevronDown, Download, Filter, Pencil
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIntelligence } from '../contexts';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import type { IntelligenceDocument, Protocol } from '../types';
import { PageHeader, fieldCls, chipCls, primaryBtnCls } from '../../ui';
import { DOCUMENT_TYPES, isRegistryProjection } from '../types';

interface Site {
  id: string;
  name: string;
  code: string;
}

// Multi-select dropdown component
interface MultiSelectOption {
  id: string;
  label: string;
}

interface MultiSelectDropdownProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

function MultiSelectDropdown({ label, options, selected, onChange, placeholder = 'All' }: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter(s => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const clearAll = () => onChange([]);

  const displayText = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? options.find(o => o.id === selected[0])?.label || '1 selected'
      : `${selected.length} selected`;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 ${
          selected.length > 0
            ? 'bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:border-primary-700 dark:text-primary-400'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'
        }`}
      >
        <Filter size={14} />
        <span>{label}: {displayText}</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg dark:bg-gray-800 dark:border-gray-700">
          <div className="p-2 border-b border-gray-100 flex items-center justify-between dark:border-gray-700">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
            {selected.length > 0 && (
              <button
                onClick={clearAll}
                className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="max-h-60 overflow-y-auto p-1">
            {options.length === 0 ? (
              <p className="text-sm text-gray-500 p-2 dark:text-gray-400">No options available</p>
            ) : (
              options.map(option => (
                <label
                  key={option.id}
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer dark:hover:bg-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(option.id)}
                    onChange={() => toggleOption(option.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  <span className="text-sm text-gray-700 truncate dark:text-gray-200">{option.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

type Scope = 'global' | 'network' | 'site' | 'protocol';

// (UploadModal removed — uploads moved to eReg, P5 §8-P5.)
interface VersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: IntelligenceDocument | null;
}

function VersionHistoryModal({ isOpen, onClose, document }: VersionHistoryModalProps) {
  const [versions, setVersions] = useState<IntelligenceDocument[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && document) {
      loadVersions();
    }
  }, [isOpen, document]);

  const loadVersions = async () => {
    if (!document) return;
    setLoading(true);
    try {
      const response = await api.get(`/api/intelligence/documents/${document.id}/versions`);
      setVersions(response.data || []);
    } catch (error) {
      console.error('Error loading versions:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !document) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col dark:bg-gray-800">
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Version History</h2>
          <button onClick={onClose} className="p-1 rounded text-gray-500 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600 mb-4 dark:text-gray-400">{document.title || document.filename}</p>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-primary-600 dark:text-primary-400" size={24} />
            </div>
          ) : (
            <div className="space-y-2">
              {versions.map((v) => (
                <div
                  key={v.id}
                  className={`p-3 rounded-lg border ${
                    v.is_current ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100">Version {v.version}</span>
                      {v.is_current && (
                        <span className="text-xs bg-primary-600 text-white px-2 py-0.5 rounded-full">Current</span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(v.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 dark:text-gray-400">{v.filename}</p>
                  {v.replaced_at && (
                    <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
                      Replaced on {new Date(v.replaced_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded-lg transition-colors duration-150 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// (BatchUploadModal removed — uploads moved to eReg, P5 §8-P5.)

export function Documents() {
  const { protocols } = useIntelligence();
  const { currentSite, user } = useAuth();
  const [documents, setDocuments] = useState<IntelligenceDocument[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | Scope>('all');
  const [selectedProtocols, setSelectedProtocols] = useState<string[]>([]);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<IntelligenceDocument | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const userRole = user?.role || 'basic_user';
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = ['super_admin', 'administrator'].includes(userRole);

  useEffect(() => {
    loadDocuments();
    loadSites();
  }, [currentSite]);

  const loadSites = async () => {
    try {
      const response = await api.get('/api/sites');
      setSites(response.data || []);
    } catch (error) {
      console.error('Error loading sites:', error);
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/intelligence/documents');
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this document? All associated chunks will be removed.')) {
      return;
    }

    try {
      await api.delete(`/api/intelligence/documents/${id}`);
      await loadDocuments();
    } catch (error: any) {
      console.error('Error deleting document:', error);
      alert(error.response?.data?.error || 'Failed to delete document. Please try again.');
    }
  };

  const handleRename = async (doc: IntelligenceDocument) => {
    const current = doc.title || doc.filename;
    const next = window.prompt('Rename document', current);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === current) return;

    try {
      await api.patch(`/api/intelligence/documents/${doc.id}`, { title: trimmed });
      await loadDocuments();
    } catch (error: any) {
      console.error('Error renaming document:', error);
      alert(error.response?.data?.error || 'Failed to rename document.');
    }
  };

  const handleReprocess = async (id: string) => {
    try {
      await api.post(`/api/intelligence/documents/${id}/reprocess`);
      await loadDocuments();
    } catch (error) {
      console.error('Error reprocessing document:', error);
      alert('Failed to reprocess document. Please try again.');
    }
  };

  const handleDownload = async (doc: IntelligenceDocument) => {
    try {
      const response = await api.get(`/api/intelligence/documents/${doc.id}/download`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading document:', error);
      alert(error.response?.data?.error || 'Failed to download document.');
    }
  };

  const openVersionHistory = (doc: IntelligenceDocument) => {
    setSelectedDocument(doc);
    setVersionModalOpen(true);
  };

  const getScopeIcon = (scope: string) => {
    switch (scope) {
      case 'global': return <Globe size={16} className="text-blue-600" />;
      case 'network': return <Building2 size={16} className="text-green-600" />;
      case 'site': return <MapPin size={16} className="text-orange-600" />;
      case 'protocol': return <FolderOpen size={16} className="text-primary-600" />;
      default: return <FileText size={16} className="text-gray-600" />;
    }
  };

  const getScopeLabel = (scope: string) => {
    switch (scope) {
      case 'global': return 'Global Documents';
      case 'network': return 'Network Documents';
      case 'site': return 'Site Documents';
      case 'protocol': return 'Protocol Documents';
      default: return 'Documents';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
            <CheckCircle size={12} />
            Processed
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
            <Loader2 size={12} className="animate-spin" />
            Processing
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
            <XCircle size={12} />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
            Pending
          </span>
        );
    }
  };

  const canManageDocument = (doc: IntelligenceDocument) => {
    // Registry projections mirror eReg — read-only here, managed in eReg (P3).
    if (isRegistryProjection(doc)) return false;
    if (isSuperAdmin) return true;
    if (doc.scope === 'global') return false; // Only super_admin can manage global
    if (doc.scope === 'network' && !isAdmin) return false; // Only admin+ can manage network
    // For site/protocol scope, all users with access can manage
    return true;
  };

  const filteredDocuments = documents.filter(doc => {
    // Scope filter
    if (filter !== 'all' && doc.scope !== filter) return false;

    // Protocol filter (multi-select)
    if (selectedProtocols.length > 0 && !selectedProtocols.includes(doc.protocol_id || '')) {
      return false;
    }

    // Site filter (multi-select)
    if (selectedSites.length > 0 && !selectedSites.includes(doc.site_id || '')) {
      return false;
    }

    return true;
  });

  // Build filter options
  const protocolOptions: MultiSelectOption[] = protocols.map(p => ({
    id: p.id,
    label: `${p.protocol_number}${p.study_nickname ? ` - ${p.study_nickname}` : ''}`,
  }));

  const siteOptions: MultiSelectOption[] = sites.map(s => ({
    id: s.id,
    label: s.name || s.code,
  }));

  // Group documents by scope
  const groupedDocuments = filteredDocuments.reduce((acc, doc) => {
    if (!acc[doc.scope]) acc[doc.scope] = [];
    acc[doc.scope].push(doc);
    return acc;
  }, {} as Record<string, IntelligenceDocument[]>);

  const scopeOrder: Scope[] = ['global', 'network', 'site', 'protocol'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Uploads moved to eReg (P5 §8-P5 — single intake surface). */}
      <PageHeader
        className="mb-6"
        title="Document Library"
        icon={FileText}
        subtitle="Documents for AI-powered Q&A — uploads now happen in eReg"
        actions={
          isAdmin ? (
            <Link to="/ereg/intake" className={primaryBtnCls}>
              <Upload size={16} />
              Upload in eReg
            </Link>
          ) : undefined
        }
      />

      <div className="mb-6 rounded-xl border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
        Document uploads have moved to eReg: study documents upload into the study's
        binder, SOPs and cross-site guidance live in{' '}
        <Link to="/ereg/network" className="font-medium underline">
          eReg Network Documents
        </Link>
        , and bulk intake (with AI classification) is{' '}
        <Link to="/ereg/intake" className="font-medium underline">
          eReg Intake
        </Link>
        . Documents listed here stay readable and feed chat as before.
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6 flex-wrap items-center">
        {/* Scope filter */}
        {(['all', 'global', 'network', 'site', 'protocol'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={chipCls(filter === f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}

        <div className="w-px h-6 bg-gray-300 mx-2 dark:bg-gray-600" />

        {/* Protocol filter */}
        <MultiSelectDropdown
          label="Protocol"
          options={protocolOptions}
          selected={selectedProtocols}
          onChange={setSelectedProtocols}
        />

        {/* Site filter */}
        <MultiSelectDropdown
          label="Site"
          options={siteOptions}
          selected={selectedSites}
          onChange={setSelectedSites}
        />

        {/* Clear filters */}
        {(selectedProtocols.length > 0 || selectedSites.length > 0) && (
          <button
            onClick={() => {
              setSelectedProtocols([]);
              setSelectedSites([]);
            }}
            className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Document list */}
      {filteredDocuments.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
          <FileText size={48} className="mx-auto text-gray-300 mb-4 dark:text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">No documents yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Upload documents to enable AI-powered Q&A</p>
        </div>
      ) : (
        <div className="space-y-6">
          {scopeOrder.map((scope) => {
            const docs = groupedDocuments[scope] || [];
            if (docs.length === 0) return null;

            return (
              <div key={scope}>
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2 dark:text-gray-400">
                  {getScopeIcon(scope)}
                  {getScopeLabel(scope)}
                  <span className="text-gray-400 font-normal dark:text-gray-500">({docs.length})</span>
                </h2>
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200 dark:bg-gray-800/50 dark:border-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Document</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Version</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Chunks</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Uploaded</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {docs.map((doc) => (
                        <tr key={doc.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <FileText size={20} className="text-gray-400" />
                              <div>
                                <p className="flex items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
                                  {doc.title || doc.filename}
                                  {isRegistryProjection(doc) && (
                                    <span
                                      className="inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
                                      title="Managed in eReg — this row mirrors the registry document"
                                    >
                                      eReg
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{doc.filename}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => openVersionHistory(doc)}
                              className="inline-flex items-center gap-1 text-sm text-gray-600 transition-colors duration-150 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400"
                            >
                              v{doc.version}
                              <History size={14} />
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(doc.processing_status)}
                            {doc.processing_error && (
                              <p className="text-xs text-red-600 mt-1 max-w-xs truncate dark:text-red-400" title={doc.processing_error}>
                                {doc.processing_error}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            {doc.chunk_count > 0 ? `${doc.chunk_count} chunks` : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {new Date(doc.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleDownload(doc)}
                                className="p-1 text-gray-400 transition-colors duration-150 hover:text-green-600 dark:hover:text-green-400"
                                title="Download"
                              >
                                <Download size={16} />
                              </button>
                              {canManageDocument(doc) && (
                                <>
                                  <button
                                    onClick={() => handleRename(doc)}
                                    className="p-1 text-gray-400 transition-colors duration-150 hover:text-primary-600 dark:hover:text-primary-400"
                                    title="Rename document"
                                  >
                                    <Pencil size={16} />
                                  </button>
                                  {doc.processing_status === 'failed' && (
                                    <button
                                      onClick={() => handleReprocess(doc.id)}
                                      className="p-1 text-gray-400 transition-colors duration-150 hover:text-blue-600 dark:hover:text-blue-400"
                                      title="Reprocess"
                                    >
                                      <RefreshCw size={16} />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDelete(doc.id)}
                                    className="p-1 text-gray-400 transition-colors duration-150 hover:text-red-600 dark:hover:text-red-400"
                                    title="Delete"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      <VersionHistoryModal
        isOpen={versionModalOpen}
        onClose={() => {
          setVersionModalOpen(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
      />
    </div>
  );
}
